import { TranslocoModule, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'core';
import { DeliveryDestinationNote, DeliveryDestinations } from 'customers';
import { CartService } from 'products';
import { switchMap } from 'rxjs';
import { OrderBusinessType, OrderSection } from '../../interfaces/order-proposal.model';
import { OrderProposalService } from '../../services/order-proposal.service';
import { DeliveryNoteDialog } from './delivery-note-dialog/delivery-note-dialog';

interface ShippingPaymentSection {
  type: OrderBusinessType;
  label: string;
  section: OrderSection;
}

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-shipping-payment',
  imports: [MatButtonModule, TranslocoModule],
  templateUrl: './shipping-payment.html',
  styleUrl: './shipping-payment.scss',
})
export class ShippingPayment implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly cartService = inject(CartService);
  private readonly proposalService = inject(OrderProposalService);

  // Le tipologie da gestire sono quelle delle righe del carrello della proposta
  readonly sections = computed<ShippingPaymentSection[]>(() => {
    const proposal = this.proposalService.proposal();
    const rows = (this.cartService.cart()?.righe ?? []).filter(row => !row.archiviata);
    if (!proposal) {
      return [];
    }

    return [
      { type: 'Oil' as const, label: 'OIL', section: proposal.oil },
      { type: 'NonOil' as const, label: 'NON OIL', section: proposal.nonOil },
    ].filter(item => rows.some(row => row.business === item.type));
  });

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.nuovaPropostaDOrdine');

    this.proposalService.ensure(this.route.snapshot.queryParamMap.get('id') ?? '')
      .pipe(switchMap(proposal => this.cartService.getCart(proposal.carrello.id)))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }

  addressLabel(destination: DeliveryDestinations | null): string | null {
    return destination ? `${destination.indirizzo}, ${destination.cap} ${destination.citta} (${destination.provincia})` : null;
  }

  // La nota c'è se il destino ha almeno un dato da riportare
  hasNote(destination: DeliveryDestinations | null): boolean {
    const nota = destination?.nota;
    return !!nota && (!!nota.sponda || [nota.notaLibera, nota.orari, nota.contatto, nota.giorni].some(value => !!value));
  }

  paymentLabel(section: OrderSection): string {
    if (!section.pagamento) {
      return translate<string>('orders.ui.selezionaLaModalitaDiPagamento');
    }
    return section.pagamentoAbituale ? `${section.pagamento} (default)` : section.pagamento;
  }

  // Il foglio dei codici cliente: la scelta confermata si scrive subito
  editCustomerCodes(): void {
    this.proposalService.chooseCustomerCodes().subscribe();
  }

  openNote(note: DeliveryDestinationNote): void {
    this.dialog.open(DeliveryNoteDialog, { width: '400px', data: note });
  }

  editShipping(type: OrderBusinessType): void {
    void this.router.navigate(['/new-order-proposal/select-destination'], {
      queryParams: { id: this.proposalService.proposal()?.id, type },
    });
  }

  editPayment(type: OrderBusinessType): void {
    void this.router.navigate(['/new-order-proposal/select-payment'], {
      queryParams: { id: this.proposalService.proposal()?.id, type },
    });
  }

  // Sempre attivo: ciò che manca alla proposta lo dice l'invio
  continueToSummary(): void {
    void this.router.navigate(['/new-order-proposal/summary'], { queryParams: { id: this.proposalService.proposal()?.id } });
  }
}
